import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppLogger, AppLoggerService } from '../../core/logging';

/**
 * Smart back navigation for pages that want to prefer browser history, but avoid
 * going back into transient routes like edit/create forms.
 *
 * Contract:
 * - Keeps a small in-app navigation stack (URLs incl. query params)
 * - Can compute a "previous" URL while skipping configured routes
 * - Falls back to Location.back() when there's no better target
 */
@Injectable({
  providedIn: 'root'
})
export class SmartBackService {

  private readonly log: AppLogger;

  /** Last few visited URLs (in-app only). */
  private readonly stack: string[] = [];

  /** How many entries to keep (prevents unbounded growth). */
  private readonly maxStackSize = 50;

  /**
   * Routes that should not be returned to via "smart back".
   * NOTE: keep this conservative; prefer adding explicit patterns for known transient pages.
   */
  private readonly skipUrlPatterns: RegExp[] = [
    // product edit / create
    /\/products\/[^/]+\/edit(\?|$)/,
    /\/products\/new(\?|$)/,
    // legacy/alternate product edit routes (in case)
    /\/product\/[^/]+\/edit(\?|$)/,
    /\/product\/new(\?|$)/,
    // recipe edit / create
    /\/recipe\/[^/]+\/edit(\?|$)/,
    /\/recipe\/new(\?|$)/
  ];

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(SmartBackService);

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(e => {
        const url = (e as NavigationEnd).urlAfterRedirects;
        this.log.trace('NavigationEnd', { url });
        this.push(url);
      });

    this.log.trace('Debug enabled', { skipUrlPatterns: this.skipUrlPatterns.map(p => p.toString()) });
  }

  /**
   * Navigate "back", skipping transient routes when possible.
   * If no in-app target exists, falls back to Location.back().
   */
  back(options?: { fallbackUrl?: string }): void {
    const current = this.router.url;
    const target = this.getBackUrl();

    this.log.trace('back() called', { current, stack: [...this.stack] });
    this.log.trace('back() computed target', { target });

    if (target) {
      this.log.trace('Navigating to target via router.navigateByUrl', { target });
      void this.router.navigateByUrl(target);
      return;
    }

    // For deep-links (first page) we can't do better than browser back.
    // If a fallback is provided, use it instead of leaving the app.
    if (options?.fallbackUrl) {
      this.log.trace('No target found. Navigating to fallbackUrl', { fallbackUrl: options.fallbackUrl });
      void this.router.navigateByUrl(options.fallbackUrl);
      return;
    }

    this.log.trace('No target/fallback. Falling back to Location.back()');
    this.location.back();
  }

  /**
   * Returns the best in-app back target URL, or null.
   * The current URL is excluded; subsequent URLs are scanned backwards.
   */
  getBackUrl(): string | null {
    if (this.stack.length < 2) {
      this.log.trace('getBackUrl(): stack too small', { stack: [...this.stack] });
      return null;
    }

    const current = this.router.url;

    // stack = [..., previous, current]
    for (let i = this.stack.length - 2; i >= 0; i--) {
      const candidate = this.stack[i];
      const skip = this.shouldSkip(candidate);
      // "Back" should reach a different page, not a different query-param state
      // of the current one (e.g. a recipe reopened at another portion size via
      // ?servings=…). Skip candidates that share the current path.
      const samePageAsCurrent = this.samePath(candidate, current);
      this.log.trace('getBackUrl(): candidate scanned', { candidate, skip, samePageAsCurrent });

      if (!skip && !samePageAsCurrent) {
        return candidate;
      }
    }

    this.log.trace('getBackUrl(): no suitable candidate found');
    return null;
  }

  private shouldSkip(url: string): boolean {
    return this.skipUrlPatterns.some(p => p.test(url));
  }

  /** Whether two URLs point at the same route path, ignoring query string and
   *  fragment (so `/recipe/5` and `/recipe/5?servings=6` count as the same). */
  private samePath(a: string, b: string): boolean {
    const path = (url: string) => url.split(/[?#]/, 1)[0];
    return path(a) === path(b);
  }

  private push(url: string): void {
    const last = this.stack[this.stack.length - 1];
    if (last === url) {
      this.log.trace('push(): ignoring duplicate url', { url });
      return;
    }

    this.stack.push(url);
    this.log.trace('push(): pushed url', { url, stack: [...this.stack] });

    if (this.stack.length > this.maxStackSize) {
      this.stack.splice(0, this.stack.length - this.maxStackSize);
      this.log.trace('push(): trimmed stack', { maxStackSize: this.maxStackSize, stackSize: this.stack.length });
    }
  }
}
